import { cn } from "../../../lib/utils";
import {
  Image,
  AlertTriangle,
  CheckCircle2,
  Search,
  ExternalLink,
  Hash,
  FileType,
  Server,
  Globe,
  XCircle,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../table";

// Types
type FaviconCategory =
  | "cms" | "framework" | "server" | "security_tool" | "network_device"
  | "cloud_service" | "database" | "monitoring" | "vpn_proxy"
  | "email" | "control_panel" | "devops" | "custom";

type RiskLevel = "info" | "low" | "medium" | "high" | "critical";

interface KnownFavicon {
  name: string;
  category: FaviconCategory;
  description?: string;
  risk_level: RiskLevel;
  cves: string[];
}

// Web endpoint discovered from portscan/service-probe
interface WebEndpoint {
  url: string;
  hostname?: string;
  ip?: string;
  port: number;
  is_https: boolean;
  source?: string;
}

// Enhanced per-endpoint favicon result
interface EndpointFavicon {
  endpoint: WebEndpoint;
  label: string;
  favicon_found: boolean;
  favicon_url?: string;
  hash?: number;
  hash_hex?: string;
  file_size?: number;
  content_type?: string;
  known_match?: KnownFavicon;
  shodan_query?: string;
  error?: string;
}

// Subdomain favicon result
interface SubdomainFavicon {
  subdomain: string;
  reachable: boolean;
  hash?: number;
  hash_hex?: string;
  favicon_url?: string;
  known_match?: KnownFavicon;
  error?: string;
}

export interface FaviconModuleData {
  score?: number;
  target?: string;
  favicon_url?: string;
  favicon_found?: boolean;
  hash?: number;
  hash_hex?: string;
  file_size?: number;
  content_type?: string;
  known_match?: KnownFavicon;
  shodan_query?: string;
  alternate_urls_checked?: string[];
  issues?: Array<string | Issue>;
  // New: per-endpoint results
  endpoint_results?: EndpointFavicon[];
  // Legacy: per-subdomain results
  subdomain_results?: SubdomainFavicon[];
  total_hosts_scanned?: number;
  unique_hashes?: number;
}

export interface FaviconModuleViewProps {
  data: FaviconModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Category config
const CATEGORY_CONFIG: Record<FaviconCategory, { label: string; color: string }> = {
  cms: { label: "CMS", color: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
  framework: { label: "Framework", color: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  server: { label: "Server", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" },
  security_tool: { label: "Security Tool", color: "bg-red-500/10 text-red-400 border-red-500/30" },
  network_device: { label: "Network Device", color: "bg-orange-500/10 text-orange-400 border-orange-500/30" },
  cloud_service: { label: "Cloud Service", color: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
  database: { label: "Database", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  monitoring: { label: "Monitoring", color: "bg-pink-500/10 text-pink-400 border-pink-500/30" },
  vpn_proxy: { label: "VPN/Proxy", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  email: { label: "Email", color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30" },
  control_panel: { label: "Control Panel", color: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
  devops: { label: "DevOps", color: "bg-teal-500/10 text-teal-400 border-teal-500/30" },
  custom: { label: "Custom", color: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
};

// Risk level config
const RISK_CONFIG: Record<RiskLevel, { label: string; color: string; bgColor: string }> = {
  critical: { label: "Critical", color: "text-red-400", bgColor: "bg-red-500/15 border-red-500/40" },
  high: { label: "High", color: "text-orange-400", bgColor: "bg-orange-500/15 border-orange-500/40" },
  medium: { label: "Medium", color: "text-amber-400", bgColor: "bg-amber-500/15 border-amber-500/40" },
  low: { label: "Low", color: "text-cyan-400", bgColor: "bg-cyan-500/15 border-cyan-500/40" },
  info: { label: "Info", color: "text-slate-400", bgColor: "bg-slate-500/15 border-slate-500/40" },
};

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "info";
      if (issue.toLowerCase().includes("c2") || issue.toLowerCase().includes("cobalt")) {
        severity = "critical";
      } else if (issue.toLowerCase().includes("admin") || issue.toLowerCase().includes("management")) {
        severity = "high";
      } else if (issue.toLowerCase().includes("default") || issue.toLowerCase().includes("exposed")) {
        severity = "medium";
      }
      return { id: `favicon-issue-${index}`, severity, message: issue, module: "favicon" };
    }
    return issue;
  });
}

// Format file size
function formatFileSize(bytes?: number): string {
  if (!bytes) return "Unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FaviconModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: FaviconModuleViewProps) {
  const issues = normalizeIssues(data.issues);

  // Filter subdomain results to only show those with favicons
  const subdomainsWithFavicons = data.subdomain_results?.filter(sub => sub.hash) ?? [];
  const knownMatch = data.known_match;
  const riskConfig = knownMatch ? RISK_CONFIG[knownMatch.risk_level] : null;
  const categoryConfig = knownMatch ? CATEGORY_CONFIG[knownMatch.category] : null;

  return (
    <ModuleContainer
      moduleName="favicon"
      icon={Image}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Favicon Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                {data.favicon_found ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-slate-400" />
                )}
                <span className={cn("font-semibold", data.favicon_found ? "text-emerald-400" : "text-slate-400")}>
                  {data.favicon_found ? "Yes" : "No"}
                </span>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Hash className="h-3 w-3" /> Hash
              </CardTitle>
            </CardHeader>
            <CardContent>
              <code className="text-sm font-mono text-cyan-400">
                {data.hash_hex || data.hash?.toString() || "—"}
              </code>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <FileType className="h-3 w-3" /> Size
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{formatFileSize(data.file_size)}</p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", knownMatch && riskConfig && (knownMatch.risk_level === "critical" || knownMatch.risk_level === "high") && "border-red-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Known Match
              </CardTitle>
            </CardHeader>
            <CardContent>
              {knownMatch ? (
                <Badge variant="outline" className={cn("text-xs", riskConfig?.bgColor)}>
                  {knownMatch.name}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">Unknown</span>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Security Tool Warning */}
        {knownMatch?.category === "security_tool" && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-pulse">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-400" />
              <div>
                <h4 className="font-semibold text-red-400">Security Tool / C2 Framework Detected</h4>
                <p className="text-sm text-muted-foreground">
                  The favicon matches "{knownMatch.name}". This could indicate a security tool, penetration testing infrastructure, or potentially malicious C2 server.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Known Match Details */}
        {knownMatch && (
          <Card className={cn("bg-card/50", riskConfig?.bgColor.replace("bg-", "border-").split(" ")[0] + "/30")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn("p-2 rounded-lg border", categoryConfig?.color)}>
                    <Server className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{knownMatch.name}</CardTitle>
                    {knownMatch.description && (
                      <p className="text-sm text-muted-foreground">{knownMatch.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {categoryConfig && (
                    <Badge variant="outline" className={cn("text-xs", categoryConfig.color)}>
                      {categoryConfig.label}
                    </Badge>
                  )}
                  {riskConfig && (
                    <Badge variant="outline" className={cn("text-xs", riskConfig.bgColor)}>
                      {riskConfig.label} Risk
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            {knownMatch.cves.length > 0 && (
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">Known CVEs</p>
                <div className="flex flex-wrap gap-2">
                  {knownMatch.cves.map((cve, index) => (
                    <a
                      key={index}
                      href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1"
                    >
                      <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20">
                        {cve}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Badge>
                    </a>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Shodan Query */}
        {data.shodan_query && (
          <ModuleSection
            title="Shodan Search"
            icon={Search}
          >
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground mb-2">Find similar servers with this Shodan query:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono bg-muted/50 px-3 py-2 rounded">
                  {data.shodan_query}
                </code>
                <a
                  href={`https://www.shodan.io/search?query=${encodeURIComponent(data.shodan_query)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                >
                  <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20">
                    Search Shodan
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Badge>
                </a>
              </div>
            </div>
          </ModuleSection>
        )}

        {/* Favicon URL */}
        {data.favicon_url && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Favicon URL</p>
                <a
                  href={data.favicon_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cyan-400 hover:underline flex items-center gap-1"
                >
                  {data.favicon_url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              {data.content_type && (
                <Badge variant="outline" className="text-xs">{data.content_type}</Badge>
              )}
            </div>
          </div>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}

        {/* Discovered Favicons Table */}
        {(data.endpoint_results && data.endpoint_results.length > 0) && (
          <ModuleSection
            title="Discovered Favicons"
            icon={Globe}
            badge={
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {data.endpoint_results.length} endpoints
                </Badge>
                {data.unique_hashes !== undefined && data.unique_hashes > 0 && (
                  <Badge variant="outline" className="text-xs bg-cyan-500/10 border-cyan-500/30 text-cyan-400">
                    {data.unique_hashes} unique
                  </Badge>
                )}
              </div>
            }
          >
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[200px]">Endpoint</TableHead>
                    <TableHead className="w-[100px]">Hash</TableHead>
                    <TableHead className="w-[80px]">Size</TableHead>
                    <TableHead className="w-[120px]">Type</TableHead>
                    <TableHead>Known Match</TableHead>
                    <TableHead className="w-[80px]">Risk</TableHead>
                    <TableHead className="w-[100px] text-right">Shodan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.endpoint_results.map((ep, index) => {
                    const epRiskConfig = ep.known_match ? RISK_CONFIG[ep.known_match.risk_level] : null;
                    const epCategoryConfig = ep.known_match ? CATEGORY_CONFIG[ep.known_match.category] : null;

                    return (
                      <TableRow
                        key={index}
                        className={cn(
                          "hover:bg-muted/20",
                          ep.known_match?.risk_level === "critical" && "bg-red-500/5",
                          ep.known_match?.risk_level === "high" && "bg-orange-500/5"
                        )}
                      >
                        {/* Endpoint */}
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              {ep.favicon_found ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                              ) : ep.error ? (
                                <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                              ) : (
                                <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              )}
                              <span className="font-medium text-sm truncate max-w-[160px]" title={ep.label}>
                                {ep.label}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground truncate max-w-[180px]" title={ep.endpoint.url}>
                              {ep.endpoint.url}
                            </span>
                          </div>
                        </TableCell>

                        {/* Hash */}
                        <TableCell>
                          {ep.hash_hex ? (
                            <code className="text-xs font-mono text-cyan-400">{ep.hash_hex}</code>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Size */}
                        <TableCell>
                          <span className="text-xs">
                            {ep.file_size ? formatFileSize(ep.file_size) : "—"}
                          </span>
                        </TableCell>

                        {/* Content Type */}
                        <TableCell>
                          {ep.content_type ? (
                            <Badge variant="outline" className="text-xs font-normal">
                              {ep.content_type.split(";")[0]}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Known Match */}
                        <TableCell>
                          {ep.known_match ? (
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className={cn("text-xs", epCategoryConfig?.color)}>
                                {ep.known_match.name}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">Unknown</span>
                          )}
                        </TableCell>

                        {/* Risk Level */}
                        <TableCell>
                          {ep.known_match && epRiskConfig ? (
                            <Badge variant="outline" className={cn("text-xs", epRiskConfig.bgColor)}>
                              {epRiskConfig.label}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Shodan Link */}
                        <TableCell className="text-right">
                          {ep.shodan_query ? (
                            <a
                              href={`https://www.shodan.io/search?query=${encodeURIComponent(ep.shodan_query)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex"
                            >
                              <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20">
                                <Search className="h-3 w-3 mr-1" />
                                Search
                              </Badge>
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </ModuleSection>
        )}

        {/* Subdomain Favicons Table - only shows subdomains with favicons */}
        {(!data.endpoint_results || data.endpoint_results.length === 0) &&
          subdomainsWithFavicons.length > 0 && (
          <ModuleSection
            title="Subdomain Favicons"
            icon={Globe}
            badge={
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {subdomainsWithFavicons.length} with favicons
                </Badge>
                {data.total_hosts_scanned && (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    {data.total_hosts_scanned} scanned
                  </Badge>
                )}
              </div>
            }
          >
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[50px]">Icon</TableHead>
                    <TableHead>Subdomain</TableHead>
                    <TableHead className="w-[100px]">Hash</TableHead>
                    <TableHead>Known Match</TableHead>
                    <TableHead className="w-[80px]">Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subdomainsWithFavicons.map((sub, index) => {
                    const subRiskConfig = sub.known_match ? RISK_CONFIG[sub.known_match.risk_level] : null;
                    const subCategoryConfig = sub.known_match ? CATEGORY_CONFIG[sub.known_match.category] : null;

                    return (
                      <TableRow
                        key={index}
                        className={cn(
                          "hover:bg-muted/20",
                          sub.known_match?.risk_level === "critical" && "bg-red-500/5"
                        )}
                      >
                        <TableCell>
                          {sub.favicon_url ? (
                            <a
                              href={sub.favicon_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={sub.favicon_url}
                                alt={`${sub.subdomain} favicon`}
                                className="w-6 h-6 rounded border border-border bg-muted/50 object-contain"
                                onError={(e) => {
                                  // Hide broken images
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </a>
                          ) : (
                            <div className="w-6 h-6 rounded border border-border bg-muted/30 flex items-center justify-center">
                              <Image className="w-3 h-3 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{sub.subdomain}</span>
                            {sub.favicon_url && (
                              <a
                                href={sub.favicon_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-cyan-400 hover:underline truncate max-w-[200px]"
                                title={sub.favicon_url}
                              >
                                {sub.favicon_url.replace(/^https?:\/\/[^/]+/, '')}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs font-mono text-cyan-400">
                            {sub.hash_hex || sub.hash?.toString(16).padStart(8, "0")}
                          </code>
                        </TableCell>
                        <TableCell>
                          {sub.known_match ? (
                            <Badge variant="outline" className={cn("text-xs", subCategoryConfig?.color)}>
                              {sub.known_match.name}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Unknown</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {sub.known_match && subRiskConfig ? (
                            <Badge variant="outline" className={cn("text-xs", subRiskConfig.bgColor)}>
                              {subRiskConfig.label}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

FaviconModuleView.displayName = "FaviconModuleView";
